import { z } from 'zod';

const BASE_URL = import.meta.env.VITE_API_URL ?? '/api/v1';

/** Backend error envelope: {"error": {code, message, details?, request_id?}} */
export const apiErrorSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.record(z.unknown()).optional(),
    request_id: z.string().optional(),
  }),
});

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: Record<string, unknown>,
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }

  get isUnauthorized() {
    return this.status === 401;
  }
  get isForbidden() {
    return this.status === 403;
  }
  get isValidation() {
    return this.status === 422;
  }
  get isPeriodClosed() {
    return this.code === 'PERIOD_CLOSED';
  }
  get isDuplicateImport() {
    return this.code === 'DUPLICATE_IMPORT';
  }
}

/** Read the non-HttpOnly CSRF cookie the server sets alongside the session. */
function csrfToken(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)ramenten_csrf=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * A schema that parses an unknown wire payload into T.
 *
 * The third parameter is Zod's Input type. Left at its default it would equal
 * T, which rejects any schema using `.default()` or `.optional()` — those
 * legitimately accept a narrower input than they produce. What arrives over the
 * network is `unknown`; what comes out is exactly T.
 */
type ResponseSchema<T> = z.ZodType<T, z.ZodTypeDef, unknown>;

interface RequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
  formData?: FormData;
}

/**
 * All requests send cookies. The auth token lives in an HttpOnly cookie the
 * JavaScript here cannot read — there is deliberately no token in storage.
 */
export async function request<T>(
  path: string,
  schema: ResponseSchema<T>,
  options: RequestOptions = {},
): Promise<T> {
  const { method = 'GET', body, signal, formData } = options;
  const headers: Record<string, string> = {};

  if (method !== 'GET' && method !== 'HEAD') {
    const token = csrfToken();
    if (token) headers['X-CSRF-Token'] = token;
  }
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  const response = await fetch(`${BASE_URL}${path}`, {
    method,
    headers,
    credentials: 'include',
    signal,
    body: formData ?? (body !== undefined ? JSON.stringify(body) : undefined),
  });

  // 204 No Content: there is no body to validate.
  if (response.status === 204) return schema.parse(undefined as unknown as T);

  const text = await response.text();
  const payload: unknown = text ? JSON.parse(text) : null;

  if (!response.ok) {
    const parsed = apiErrorSchema.safeParse(payload);
    if (parsed.success) {
      const { code, message, details, request_id } = parsed.data.error;
      throw new ApiRequestError(response.status, code, message, details, request_id);
    }
    throw new ApiRequestError(response.status, 'http_error', `Ошибка ${response.status}`);
  }

  const result = schema.safeParse(payload);
  if (!result.success) {
    throw new ApiRequestError(
      response.status,
      'invalid_response',
      'Неожиданный формат ответа сервера',
      { issues: result.error.issues },
    );
  }
  return result.data;
}

export const api = {
  get: <T>(path: string, schema: ResponseSchema<T>, signal?: AbortSignal) =>
    request(path, schema, { signal }),
  post: <T>(path: string, schema: ResponseSchema<T>, body?: unknown) =>
    request(path, schema, { method: 'POST', body }),
  patch: <T>(path: string, schema: ResponseSchema<T>, body?: unknown) =>
    request(path, schema, { method: 'PATCH', body }),
  delete: <T>(path: string, schema: ResponseSchema<T>) =>
    request(path, schema, { method: 'DELETE' }),
  upload: <T>(path: string, schema: ResponseSchema<T>, formData: FormData) =>
    request(path, schema, { method: 'POST', formData }),
};
